<!-- 入库数据智能管理 -->
<template>
  <div class="inbound-smart-page">
    <section class="page-head">
      <div>
        <h2>入库数据智能管理</h2>
      </div>
      <div class="head-actions">
        <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
      </div>
    </section>

    <section class="filter-panel">
      <el-input v-model.trim="query.trainNumber" clearable placeholder="车牌号" @keyup.enter.native="search" />
      <el-input v-model.trim="query.planNo" clearable placeholder="计划/派车单号" @keyup.enter.native="search" />
      <el-select v-model="query.custNo" clearable filterable placeholder="全部货主">
        <el-option
          v-for="item in ownerList"
          :key="item.id"
          :label="item.deptName"
          :value="item.id"
        />
      </el-select>
      <el-select v-model="query.smartTag" clearable placeholder="全部结论" @change="search">
        <el-option label="可信入库" value="trusted" />
        <el-option label="超重预警" value="overload" />
        <el-option label="无法判定" value="unable_judge" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        class="date-range-picker"
        type="daterange"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        range-separator="至"
      />
      <el-button
        class="risk-filter-btn"
        :class="{ active: query.onlyRisk }"
        icon="el-icon-warning-outline"
        @click="toggleRisk"
      >
        {{ query.onlyRisk ? '查看全部' : '只看风险' }}
      </el-button>
    </section>

    <section class="stat-grid">
      <div
        v-for="item in statCards"
        :key="item.key"
        class="stat-card"
        :class="item.type"
      >
        <div class="stat-label">{{ item.label }}</div>
        <div class="stat-value">
          {{ item.value }}<span>{{ item.unit }}</span>
        </div>
        <div class="stat-foot">{{ item.foot }}</div>
      </div>
    </section>

    <section class="panel rule-panel">
      <div class="panel-head">
        <div>
          <h3>轴重校验规则</h3>
        </div>
      </div>
      <div v-if="ruleList.length" class="rule-grid">
        <div v-for="item in ruleList" :key="item.name" class="rule-item">
          <div class="rule-name">{{ item.name }}</div>
          <div class="rule-value">{{ item.value }}</div>
          <el-tag size="mini" :type="tagType(item.type)">{{ ruleTagText(item.type) }}</el-tag>
        </div>
      </div>
    </section>

    <section class="work-grid">
      <div class="panel table-panel">
        <div class="panel-head">
          <div>
            <h3>入库明细</h3>
            <p>最近核验：{{ summary.scanTime || '-' }}</p>
          </div>
        </div>
        <el-table
          v-loading="loading"
          :data="tableData"
          border
          stripe
          height="540"
          empty-text="暂无数据"
        >
          <el-table-column prop="id" label="入库单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="trainNumber" label="车牌号" min-width="110" show-overflow-tooltip />
          <el-table-column prop="planNo" label="计划/派车单" min-width="150" show-overflow-tooltip />
          <el-table-column prop="custName" label="货主" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.custName || scope.row.custNo || '-' }}</template>
          </el-table-column>
          <el-table-column prop="cargoName" label="物资" min-width="110" show-overflow-tooltip />
          <el-table-column prop="axlesName" label="轴数/桥型" min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.axlesName || formatAxlesName(scope.row.axles) }}</template>
          </el-table-column>
          <el-table-column prop="grossWeight" label="毛重(吨)" min-width="105" align="right">
            <template slot-scope="scope">{{ formatWeight(scope.row.grossWeight) }}</template>
          </el-table-column>
          <el-table-column prop="grossWeightLimit" label="允许上限(吨)" min-width="118" align="right">
            <template slot-scope="scope">{{ formatWeight(scope.row.grossWeightLimit) }}</template>
          </el-table-column>
          <el-table-column prop="grossWeightOver" label="超重(吨)" min-width="100" align="right">
            <template slot-scope="scope">
              <span :class="{ 'danger-text': Number(scope.row.grossWeightOver || 0) > 0 }">
                {{ formatWeight(scope.row.grossWeightOver) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="netWeight" label="净重(吨)" min-width="100" align="right">
            <template slot-scope="scope">{{ formatWeight(scope.row.netWeight) }}</template>
          </el-table-column>
          <el-table-column prop="axleWeightStatusName" label="轴重结论" min-width="110">
            <template slot-scope="scope">
              <el-tag size="mini" :type="smartTagType(scope.row.axleWeightStatus || scope.row.smartTag)">
                {{ scope.row.axleWeightStatusName || scope.row.smartTagName || '-' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="reason" label="问题归因" min-width="230" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.axleWeightReason || scope.row.reason || '-' }}</template>
          </el-table-column>
          <el-table-column prop="action" label="处理建议" min-width="190" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.axleWeightAction || scope.row.action || '-' }}</template>
          </el-table-column>
        </el-table>
        <div class="pager">
          <span>共 {{ page.total }} 条</span>
          <el-pagination
            background
            layout="sizes, prev, pager, next"
            :current-page="page.currentPage"
            :page-size="page.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="page.total"
            @size-change="sizeChange"
            @current-change="currentChange"
          />
        </div>
      </div>

      <aside class="panel side-panel">
        <div class="panel-head compact">
          <div>
            <h3>风险归因</h3>
          </div>
          <el-button v-if="query.reasonType" type="text" @click="clearReasonFilter">清除</el-button>
        </div>
        <div class="reason-list">
          <div
            v-for="item in reasonList"
            :key="item.reasonType"
            class="reason-item"
            :class="{
              active: query.reasonType === item.reasonType,
              disabled: !item.count
            }"
            @click="selectReason(item)"
          >
            <div class="reason-title">
              <span>{{ item.title }}</span>
              <el-tag size="mini" :type="tagType(item.type)">{{ item.count || 0 }} 条</el-tag>
            </div>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </aside>
    </section>
  </div>
</template>

<script>
import { getList, getOwnerList, getSummary } from './api'
import { unwrapDictList } from '@/util/dict-list'

export default {
  name: 'car-storage-warehousing-intelligent-data-management',
  data () {
    return {
      loading: false,
      ownerList: [],
      dateRange: [],
      query: {
        businessType: 3,
        trainNumber: '',
        planNo: '',
        custNo: '',
        smartTag: '',
        reasonType: '',
        onlyRisk: false
      },
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      summary: this.emptySummary(),
      tableData: []
    }
  },
  computed: {
    statCards () {
      return [
        { key: 'totalCount', label: '入库车次', value: this.summary.totalCount || 0, unit: '车', foot: '当前筛选范围内的汽运入库流水', type: 'normal' },
        { key: 'trustedCount', label: '可信入库', value: this.summary.trustedCount || 0, unit: '车', foot: `${this.summary.trustRate || 0}% 符合校验要求`, type: 'success' },
        { key: 'overloadCount', label: '超重预警', value: this.summary.overloadCount || this.summary.blockCount || 0, unit: '车', foot: '车辆毛重超过允许范围', type: 'danger' },
        { key: 'unableJudgeCount', label: '无法判定', value: this.summary.unableJudgeCount || 0, unit: '车', foot: '缺少轴数、毛重或规则设置', type: 'warn' }
      ]
    },
    ruleList () {
      return Array.isArray(this.summary.rules) ? this.summary.rules : []
    },
    reasonList () {
      if (Array.isArray(this.summary.reasonRanks) && this.summary.reasonRanks.length) {
        return this.summary.reasonRanks.filter(item => item && item.reasonType !== 'flow_pending')
      }
      return [
        { reasonType: 'overload', title: '超重预警', count: this.summary.overloadCount || this.summary.blockCount || 0, type: 'bad', desc: '车辆毛重超过当前允许范围，需复核后处理。' },
        { reasonType: 'missing_axles', title: '缺少轴数', count: 0, type: 'warn', desc: '当前记录缺少车辆轴数，暂不能完成校验。' },
        { reasonType: 'missing_weight', title: '缺少毛重', count: 0, type: 'warn', desc: '当前记录缺少有效毛重，需确认过磅数据是否已同步。' },
        { reasonType: 'missing_rule', title: '规则未完善', count: 0, type: 'warn', desc: '当前轴数还没有设置校验上限，请联系管理员完善规则。' },
        { reasonType: 'trusted', title: '可信入库', count: this.summary.trustedCount || 0, type: 'ok', desc: '车辆信息和重量数据完整，毛重在允许范围内。' }
      ]
    }
  },
  created () {
    this.setDefaultDateRange()
    this.loadOwnerList()
    this.loadData()
  },
  methods: {
    emptySummary () {
      return {
        totalCount: 0,
        totalWeight: 0,
        trustedCount: 0,
        trustRate: 0,
        exceptionCount: 0,
        repairCount: 0,
        reviewCount: 0,
        blockCount: 0,
        overloadCount: 0,
        unableJudgeCount: 0,
        scanTime: '',
        rules: [],
        reasonRanks: []
      }
    },
    setDefaultDateRange () {
      const end = new Date()
      const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000)
      this.dateRange = [this.formatDate(start), this.formatDate(end)]
    },
    formatDate (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    buildParams (withPage) {
      const params = {
        businessType: 3,
        trainNumber: this.query.trainNumber,
        planNo: this.query.planNo,
        custNo: this.query.custNo,
        smartTag: this.query.smartTag,
        reasonType: this.query.reasonType,
        onlyRisk: this.query.onlyRisk
      }
      if (withPage) {
        params.page = this.page.currentPage
        params.size = this.page.pageSize
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = `${this.dateRange[0]} 00:00:00`
        params.endTime = `${this.dateRange[1]} 23:59:59`
      }
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined || params[key] === false) {
          delete params[key]
        }
      })
      return params
    },
    getResponseData (res) {
      return (res && res.data && res.data.data) || {}
    },
    async loadOwnerList () {
      try {
        const res = await getOwnerList()
        this.ownerList = unwrapDictList(res)
      } catch (e) {
        this.ownerList = []
      }
    },
    async loadData () {
      this.loading = true
      try {
        const pageParams = this.buildParams(true)
        const summaryParams = this.buildParams(false)
        const resList = await Promise.all([getList(pageParams), getSummary(summaryParams)])
        const pageData = this.getResponseData(resList[0])
        const summaryData = this.getResponseData(resList[1])
        this.tableData = Array.isArray(pageData.records) ? pageData.records : []
        this.page.total = Number(pageData.total || 0)
        this.summary = Object.assign(this.emptySummary(), summaryData || {})
      } catch (e) {
        this.tableData = []
        this.summary = this.emptySummary()
        this.$message.error('汽运入库轴重核验数据加载失败')
      } finally {
        this.loading = false
      }
    },
    search () {
      this.page.currentPage = 1
      this.loadData()
    },
    resetQuery () {
      this.query = {
        businessType: 3,
        trainNumber: '',
        planNo: '',
        custNo: '',
        smartTag: '',
        reasonType: '',
        onlyRisk: false
      }
      this.page.currentPage = 1
      this.setDefaultDateRange()
      this.loadData()
    },
    toggleRisk () {
      this.query.onlyRisk = !this.query.onlyRisk
      this.search()
    },
    selectReason (item) {
      if (!item || !item.count) return
      this.query.reasonType = this.query.reasonType === item.reasonType ? '' : item.reasonType
      this.search()
    },
    clearReasonFilter () {
      this.query.reasonType = ''
      this.search()
    },
    sizeChange (size) {
      this.page.pageSize = size
      this.page.currentPage = 1
      this.loadData()
    },
    currentChange (page) {
      this.page.currentPage = page
      this.loadData()
    },
    formatAxlesName (axles) {
      if (axles === null || axles === undefined || axles === '') return '-'
      const map = {
        1: '单轴',
        2: '双轴',
        3: '三轴',
        4: '四轴',
        5: '五轴',
        6: '六轴单桥',
        7: '六轴双桥'
      }
      return map[Number(axles)] || `${axles}轴`
    },
    formatWeight (value) {
      if (value === null || value === undefined || value === '') return '-'
      const n = Number(value)
      return Number.isNaN(n) ? '-' : n.toFixed(2)
    },
    tagType (type) {
      const map = {
        ok: 'success',
        warn: 'warning',
        bad: 'danger',
        info: 'info'
      }
      return map[type] || 'info'
    },
    ruleTagText (type) {
      const map = {
        ok: '正常',
        warn: '关注',
        bad: '超限',
        info: '提示'
      }
      return map[type] || '提示'
    },
    smartTagType (tag) {
      const map = {
        trusted: 'success',
        overload: 'danger',
        unable_judge: 'warning',
        ok: 'success',
        block: 'danger',
        repair: 'warning',
        review: 'info'
      }
      return map[tag] || 'info'
    }
  }
}
</script>

<style scoped lang="scss">
.inbound-smart-page {
  width: 100%;
  min-width: 0;
  min-height: 100%;
  padding: 18px 20px 24px;
  background: #f4f7fb;
  color: #102033;
  box-sizing: border-box;
  overflow-x: hidden;
}

.inbound-smart-page * {
  box-sizing: border-box;
}

.page-head,
.filter-panel,
.panel,
.stat-card {
  background: #fff;
  border: 1px solid #dfe6f0;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, .06);
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 78px;
  padding: 16px 18px;
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  }
  p {
    margin: 6px 0 0;
    color: #637083;
    font-size: 13px;
  }
}

.head-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-panel {
  display: grid;
  grid-template-columns:
    minmax(130px, .9fr)
    minmax(150px, 1fr)
    minmax(130px, .85fr)
    minmax(130px, .85fr)
    minmax(330px, 1.5fr)
    minmax(108px, 118px);
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 14px 16px;
  ::v-deep .el-input,
  ::v-deep .el-select,
  ::v-deep .el-date-editor {
    width: 100%;
  }
  > .el-button {
    width: 100%;
    margin-left: 0;
  }
}

.date-range-picker {
  min-width: 330px;
  ::v-deep .el-range-input {
    width: 112px;
  }
  ::v-deep .el-range-separator {
    width: 24px;
    padding: 0;
    color: #30394a;
  }
}

.risk-filter-btn {
  min-width: 108px;
  border-color: #f59e0b;
  background: #fff7ed;
  color: #b45309;
  font-weight: 700;
  transition: all .18s ease;
  &:hover,
  &:focus {
    border-color: #d97706;
    background: #ffedd5;
    color: #92400e;
  }
  &.active {
    border-color: #dc2626;
    background: #dc2626;
    color: #fff;
    box-shadow: 0 6px 14px rgba(220, 38, 38, .18);
  }
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.stat-card {
  min-height: 112px;
  padding: 15px;
  &.success .stat-value,
  &.ready .stat-value {
    color: #15803d;
  }
  &.danger {
    border-color: #fecaca;
    background: #fff7f7;
    .stat-value {
      color: #dc2626;
    }
  }
  &.warn .stat-value {
    color: #d97706;
  }
  &.info .stat-value {
    color: #2563eb;
  }
}

.stat-label,
.stat-foot,
.panel-head p,
.reason-item p,
.rule-item p {
  color: #637083;
  font-size: 12px;
}

.stat-value {
  margin-top: 10px;
  font-size: 28px;
  font-weight: 800;
  span {
    margin-left: 4px;
    color: #637083;
    font-size: 13px;
    font-weight: 500;
  }
}

.stat-foot {
  margin-top: 8px;
}

.panel {
  min-width: 0;
  overflow: hidden;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid #dfe6f0;
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }
  p {
    margin: 4px 0 0;
  }
  &.compact {
    align-items: center;
  }
}

.rule-panel,
.work-grid {
  margin-top: 14px;
}

.rule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
  padding: 16px;
}

.rule-item {
  min-height: 112px;
  padding: 12px;
  border: 1px solid #dfe6f0;
  border-radius: 8px;
  background: #f8fbff;
  p {
    margin: 8px 0 0;
    line-height: 1.5;
  }
}

.rule-name {
  color: #405066;
  font-size: 13px;
  font-weight: 700;
}

.rule-value {
  display: inline-block;
  margin: 8px 8px 8px 0;
  color: #102033;
  font-size: 22px;
  font-weight: 800;
}

.empty-block {
  padding: 18px 16px;
  color: #637083;
  font-size: 13px;
}

.work-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 14px;
}

.danger-text {
  color: #dc2626;
  font-weight: 700;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  color: #637083;
}

.reason-list {
  display: grid;
  gap: 10px;
  padding: 14px;
}

.reason-item {
  padding: 12px;
  border: 1px solid #dfe6f0;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  transition: all .18s ease;
  &:hover {
    border-color: #93c5fd;
    background: #eff6ff;
  }
  &.active {
    border-color: #2563eb;
    background: #eff6ff;
  }
  &.disabled {
    cursor: default;
    opacity: .72;
    &:hover {
      border-color: #dfe6f0;
      background: #f8fafc;
    }
  }
  p {
    margin: 8px 0 0;
    line-height: 1.5;
  }
}

.reason-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.reason-title {
  font-weight: 700;
}

@media (max-width: 1280px) {
  .filter-panel {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .date-range-picker {
    min-width: 0;
  }
  .work-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .inbound-smart-page {
    padding: 12px;
  }
  .filter-panel {
    grid-template-columns: 1fr;
  }
  .page-head {
    align-items: flex-start;
  }
  .head-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .pager {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
